import * as React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { getTranslate, withLocalize } from 'react-localize-redux';
import { useParams } from 'react-router-dom';
import {
    Button, Typography, CircularProgress, makeStyles,
} from '@material-ui/core';

import * as AuthStore from '../../app/store/AuthStore';
import Auth from '../../youwell-common/Auth';
import urls from '../../app/urls';
import { programLanguages } from '../../common/constants';
import { storeLanguagePreferance } from '../../app/localization';

const useStyles = makeStyles(theme => ({
    root: {
    },

    login_container: {
        padding: theme.spacing(2),
    },

    loginBox: {
        margin: theme.spacing(2, 0),
        position: 'relative',
    },
    loginButtons: {
        margin: theme.spacing(4, 0),
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
    },
    loginButton: {
        flex: '0 0 calc(66% - 16px)',
        margin: theme.spacing(2, 1),
    },

    loading: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, 0)',
    },
    infoContainer: {
        marginTop: theme.spacing(4),
        color: theme.youwell.colors.secondary,
    },
}), { name: 'CountrySelect' });


const CountrySelect = ({ setActiveLanguage }) => {
    const classes = useStyles();
    const params = useParams();
    const dispatch = useDispatch();
    const translate = useSelector(state => getTranslate(state.localize));
    const loading = useSelector(state => state.auth.loading);
    const errorMessage = useSelector(state => [].concat(state.auth.errors).filter(val => val).join('<br/>'));
    const appConfig = useSelector(state => state.pageSettings.appConfig);
    const logoutReason = useSelector(state => state.auth.logoutReason);
    const redirect = params.redirect ? decodeURIComponent(params.redirect) : urls.inside.home;

    const [isLoggingIn, setIsLoggingIn] = React.useState(false);

    const startNewSession = language => () => {
        setIsLoggingIn(true);
        setActiveLanguage(language);
        storeLanguagePreferance(language);
        const programId = appConfig?.startProgram?.[language];
        dispatch(AuthStore.actionCreators.startNewSession({
            orgId: appConfig?.orgId,
            programId,
            programStartDate: new Date(),
            includeCurrentProgramInResponse: true,
        }, { redirect, useSessionWithoutLifetime: true }));
    };

    const isLoggedIn = Auth.isUserAuthenticated();

    React.useEffect(() => {
        if (isLoggingIn) {
            return;
        }

        if (isLoggedIn) {
            setIsLoggingIn(true);
            dispatch(AuthStore.actionCreators.getUserProfile({ redirect }));
        }
    }, [appConfig.orgId, dispatch, isLoggedIn, isLoggingIn, redirect]);

    return (
        <div className={classes.root}>
            <div className={classes.login_container}>
                <div className={classes.loginBox}>
                    {loading && (
                        <div className={classes.loading}>
                            <CircularProgress />
                        </div>
                    )}

                    <div className={classes.loginButtons}>
                        {programLanguages.map(lang => (
                            <Button key={lang.code} onClick={startNewSession(lang.code)} variant="contained" color="primary" className={classes.loginButton} disabled={loading}>
                                {`${translate('header.title', null, { language: lang.code })} - ${lang.name}`}
                            </Button>
                        ))}
                    </div>

                    <div className={classes.infoContainer}>
                        {logoutReason && (
                            <Typography color="inherit" gutterBottom>
                                {translate(`errors.${logoutReason}`, null, { onMissingTranslation: () => '' })}
                            </Typography>
                        )}
                        {errorMessage && (
                            <Typography color="error" align="center" display="block">
                                {translate(`errors.${errorMessage}`, null, { onMissingTranslation: () => `System error (${errorMessage})` })}
                            </Typography>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};

CountrySelect.propTypes = {
    setActiveLanguage: PropTypes.func.isRequired,
};

export default withLocalize(CountrySelect);
